import React, { useState, useEffect, useMemo } from 'react';
import { PatientSearchSelect } from './PatientSearchSelect';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Trash2, 
  Lock, 
  User, 
  AlertCircle, 
  Check, 
  X, 
  Settings, 
  Search, 
  PlusCircle, 
  CalendarPlus, 
  CalendarRange, 
  Ban,
  Building,
  FileText,
  MapPin,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Pencil,
  Copy,
  Video,
  ExternalLink,
  MessageSquare,
  MessageCircle,
  Repeat,
} from 'lucide-react';
import { Patient } from '../types';
import { selectablePatients } from '../utils/patientAccess';
import {
  cancelAppointment,
  fetchAppointments,
  fetchMyBookingPage,
  updateAppointment,
  type AppointmentResponse,
} from '../services/bookingApi';
import {
  createAgendaEvents,
  accessTeleconsulta,
  deleteAgendaEvent,
  fetchAgendaEvents,
  updateAgendaEvent,
  type AgendaEventPayload,
  type AgendaEventResponse,
} from '../services/agendaApi';
import { LOCATIONS_STORAGE_KEY, SERVICES_STORAGE_KEY } from './AgendaSettings';
import { scopedKey } from '../utils/scopedStorage';
import { describeError } from '../utils/describeError';
import { getGoogleMeetStatus } from '../services/googleMeetApi';
import { eventTone } from '../utils/agendaColors';
import { recordAttendanceIncome, removeAttendanceIncome } from '../services/cashBookApi';
import {
  formatCurrency,
  parseAmount,
} from '../utils/cashBook';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'appointment' | 'compromise' | 'blocking';
  date: string; // YYYY-MM-DD
  start: string; // HH:MM
  end: string;   // HH:MM
  patientId?: string;
  patientName?: string;
  modality?: 'PRESENCIAL' | 'ONLINE' | 'PRESENCIAL_ONLINE';
  locationName?: string;
  notes?: string;
  color?: 'green' | 'orange' | 'purple' | 'indigo';
  serviceValue?: string;
  recurrence?: 'NONE' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  recurrenceCount?: number;
  notifyPatient?: boolean;
  allDay?: boolean;
  professionalId?: string;
  /** Comparecimento do paciente — define a cor do evento e o lançamento no caixa. */
  status?: 'Compareceu' | 'Não Compareceu';
  /** Resposta do paciente pelos links do WhatsApp (blocos #42/#43). */
  patientResponse?: 'CONFIRMADO' | 'CANCELADO';
  /** Tipo de serviço escolhido no agendamento (de Configurações > Serviços). */
  serviceName?: string;
  /**
   * De onde o evento veio: `agenda` (criado aqui, persistido em /agenda/events)
   * ou `portal` (reserva do paciente pelo link externo, em /appointments).
   */
  source?: 'agenda' | 'portal';
  /** Bloco #56: sala do Google Meet criada para esta teleconsulta. */
  meetLink?: string;
}

/** Serviço cadastrado em Configurações da Agenda > Serviços Oferecidos. */
interface AgendaService {
  id: string;
  name: string;
  duration: number;
  price: number;
  active?: boolean;
}

const CALENDAR_CACHE_KEY = 'allodos-calendar-events';
/** Marca que os eventos antigos do localStorage já foram levados para a API. */
const CALENDAR_MIGRATED_KEY = 'allodos-calendar-events-migrated';

const isUuid = (value?: string): boolean =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const formatPrice = (value?: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2).replace('.', ',') : '';

/** Local de atendimento cadastrado em Configurações da Agenda > Locais de Atendimento. */
type AgendaLocation = {
  id: string;
  name: string;
  type: 'online' | 'presential';
  address?: string;
  active?: boolean;
  /** Bloco 57: link da sala virtual (Google Meet) do local online. */
  link?: string;
  /** Bloco 36: profissionais que atendem neste local (vazio/ausente = todos). */
  professionalIds?: string[];
};

const readLocationsFromStorage = (): AgendaLocation[] => {
  try {
    const saved = localStorage.getItem(scopedKey(LOCATIONS_STORAGE_KEY));
    const parsed = saved ? JSON.parse(saved) : null;
    if (Array.isArray(parsed)) return parsed.filter((l) => l && l.active !== false) as AgendaLocation[];
  } catch { /* cache inválido */ }
  return [];
};

const readServicesFromStorage = (): AgendaService[] => {
  try {
    const saved = localStorage.getItem(scopedKey(SERVICES_STORAGE_KEY));
    const parsed = saved ? JSON.parse(saved) : null;
    if (Array.isArray(parsed)) return parsed.filter((s) => s && s.active !== false) as AgendaService[];
  } catch { /* cache inválido */ }
  return [];
};

const readCachedEvents = (): CalendarEvent[] => {
  try {
    const saved = localStorage.getItem(scopedKey(CALENDAR_CACHE_KEY));
    const parsed = saved ? JSON.parse(saved) : null;
    if (Array.isArray(parsed)) return parsed as CalendarEvent[];
  } catch { /* cache inválido */ }
  return [];
};

const colorFor = (type: CalendarEvent['type']): CalendarEvent['color'] =>
  type === 'appointment' ? 'green' : type === 'compromise' ? 'orange' : 'purple';

const toAgendaType = (type: CalendarEvent['type']): AgendaEventPayload['type'] =>
  type === 'appointment' ? 'APPOINTMENT' : type === 'compromise' ? 'COMPROMISE' : 'BLOCKING';

const fromAgendaType = (type: AgendaEventResponse['type']): CalendarEvent['type'] =>
  type === 'APPOINTMENT' ? 'appointment' : type === 'COMPROMISE' ? 'compromise' : 'blocking';

/** Evento persistido na API → evento do calendário. */
const mapAgendaEvent = (e: AgendaEventResponse): CalendarEvent => ({
  id: e.id,
  title: e.title,
  type: fromAgendaType(e.type),
  date: e.date,
  start: e.start,
  end: e.end,
  allDay: e.allDay || undefined,
  patientId: e.patientId || undefined,
  patientName: e.patientName || undefined,
  modality: (e.modality as CalendarEvent['modality']) || undefined,
  locationName: e.locationName || undefined,
  notes: e.notes || undefined,
  serviceName: e.serviceName || undefined,
  serviceValue: e.serviceValue || undefined,
  recurrence: (e.recurrence as CalendarEvent['recurrence']) || undefined,
  recurrenceCount: e.recurrenceCount ?? undefined,
  notifyPatient: e.notifyPatient ?? undefined,
  patientResponse: e.patientResponse === 'CONFIRMADO' || e.patientResponse === 'CANCELADO' ? e.patientResponse : undefined,
  status: (e.attendance as CalendarEvent['status']) || undefined,
  professionalId: e.professionalId || undefined,
  color: colorFor(fromAgendaType(e.type)),
  source: 'agenda',
  meetLink: e.meetLink || undefined,
});

/** Reserva do portal → evento do calendário. */
const mapAppointment = (a: AppointmentResponse): CalendarEvent => ({
  id: a.id,
  // O card do calendário mostra o que vem antes do "|": o nome do paciente.
  // O serviço continua nos detalhes do agendamento.
  title: `${a.patientName || 'Paciente'} | ${a.serviceName || 'Atendimento'}`,
  type: 'appointment',
  date: a.date,
  start: a.start,
  end: a.end,
  patientId: a.patientId || undefined,
  patientName: a.patientName,
  modality: (a.modality as CalendarEvent['modality']) || 'PRESENCIAL',
  locationName: a.locationName || undefined,
  // Só a observação que o paciente escreveu; o telefone fica no cadastro.
  notes: a.notes?.trim() || undefined,
  serviceName: a.serviceName || undefined,
  // Bloco #55: preço gravado na reserva (vai para o Livro Caixa no "Compareceu").
  serviceValue: a.serviceValue || undefined,
  status: (a.attendance as CalendarEvent['status']) || undefined,
  // Bloco #54: só a confirmação do paciente pelo link (não a automática da página).
  patientResponse: a.patientResponse === 'CONFIRMADO' ? 'CONFIRMADO' : undefined,
  professionalId: a.professionalId || undefined,
  color: 'indigo',
  source: 'portal',
  meetLink: a.meetLink || undefined,
});

/** Evento do calendário → payload da API da agenda. */
const toAgendaPayload = (ev: CalendarEvent): AgendaEventPayload => ({
  type: toAgendaType(ev.type),
  title: ev.title,
  date: ev.date,
  start: ev.start,
  end: ev.end,
  allDay: !!ev.allDay,
  professionalId: ev.professionalId,
  patientId: ev.patientId,
  patientName: ev.patientName,
  modality: ev.modality,
  locationName: ev.locationName,
  notes: ev.notes,
  serviceName: ev.serviceName,
  serviceValue: ev.serviceValue,
  recurrence: ev.recurrence,
  recurrenceCount: ev.recurrenceCount,
  notifyPatient: ev.notifyPatient,
  attendance: ev.status,
});

const DEFAULT_EVENTS: CalendarEvent[] = [
  // June 1st, 2026
  {
    id: 'e1',
    title: 'LEANDRO VINICIUS GALARZA | Psicoterapia',
    type: 'appointment',
    date: '2026-06-01',
    start: '09:00',
    end: '10:00',
    patientName: 'Leandro Vinicius Galarza',
    modality: 'PRESENCIAL',
    locationName: 'Consultório Principal',
    color: 'green'
  },
  {
    id: 'e2',
    title: 'Visita Sterah',
    type: 'compromise',
    date: '2026-06-01',
    start: '10:15',
    end: '11:15',
    notes: 'Apresentação de serviços',
    color: 'orange'
  },
  {
    id: 'e3',
    title: 'Guilherme de Oliveira | Psicoterapia',
    type: 'appointment',
    date: '2026-06-01',
    start: '12:00',
    end: '13:00',
    patientName: 'Guilherme de Oliveira',
    modality: 'ONLINE',
    locationName: 'Plataforma Online',
    color: 'green'
  },
  {
    id: 'e4',
    title: 'Anhanguera',
    type: 'compromise',
    date: '2026-06-01',
    start: '13:00',
    end: '14:30',
    notes: 'Reunião de parceria',
    color: 'orange'
  },
  
  // June 2nd, 2026
  {
    id: 'e5',
    title: 'Acupuntura',
    type: 'compromise',
    date: '2026-06-02',
    start: '10:00',
    end: '11:00',
    color: 'orange'
  },
  {
    id: 'e6',
    title: 'Entrevista',
    type: 'compromise',
    date: '2026-06-02',
    start: '11:30',
    end: '12:30',
    color: 'orange'
  },
  {
    id: 'e7',
    title: 'Paciente 402TCT204 - Cristiane Gringo',
    type: 'appointment',
    date: '2026-06-02',
    start: '13:00',
    end: '14:00',
    patientName: 'Cristiane Gringo',
    modality: 'PRESENCIAL',
    color: 'green'
  },
  {
    id: 'e8',
    title: 'Paciente 292TCT292 - Rômolo Monte',
    type: 'appointment',
    date: '2026-06-02',
    start: '14:00',
    end: '15:00',
    patientName: 'Rômolo Monte',
    modality: 'PRESENCIAL',
    color: 'green'
  },
  {
    id: 'e8_extra1',
    title: 'Supervisão de Caso',
    type: 'compromise',
    date: '2026-06-02',
    start: '15:30',
    end: '16:30',
    color: 'orange'
  },
  {
    id: 'e8_extra2',
    title: 'Pilates',
    type: 'compromise',
    date: '2026-06-02',
    start: '17:00',
    end: '18:00',
    color: 'orange'
  },
  {
    id: 'e8_extra3',
    title: 'Reunião Colegiado',
    type: 'compromise',
    date: '2026-06-02',
    start: '18:30',
    end: '19:30',
    color: 'orange'
  },

  // June 3rd, 2026
  {
    id: 'e9',
    title: 'raio x pé',
    type: 'compromise',
    date: '2026-06-03',
    start: '08:00',
    end: '09:00',
    color: 'orange'
  },
  {
    id: 'e10',
    title: 'Unha',
    type: 'compromise',
    date: '2026-06-03',
    start: '09:30',
    end: '10:30',
    color: 'orange'
  },
  {
    id: 'e11',
    title: 'Paciente 424TCT424 - Ana Maria Mello',
    type: 'appointment',
    date: '2026-06-03',
    start: '12:00',
    end: '13:00',
    patientName: 'Ana Maria Mello',
    modality: 'PRESENCIAL',
    color: 'green'
  },
  {
    id: 'e12',
    title: 'Paciente 426TCT624 - Sadi Ronaldo Xavier',
    type: 'appointment',
    date: '2026-06-03',
    start: '13:00',
    end: '14:00',
    patientName: 'Sadi Ronaldo Xavier',
    modality: 'ONLINE',
    color: 'green'
  },
  {
    id: 'e12_extra1',
    title: 'Estudo de Caso',
    type: 'compromise',
    date: '2026-06-03',
    start: '14:30',
    end: '15:30',
    color: 'orange'
  },
  {
    id: 'e12_extra2',
    title: 'Dentista',
    type: 'compromise',
    date: '2026-06-03',
    start: '16:00',
    end: '17:00',
    color: 'orange'
  },
  {
    id: 'e12_extra3',
    title: 'Mentoria Clinica',
    type: 'compromise',
    date: '2026-06-03',
    start: '17:30',
    end: '18:30',
    color: 'orange'
  },
  {
    id: 'e12_extra4',
    title: 'Atendimento Social',
    type: 'appointment',
    date: '2026-06-03',
    start: '19:00',
    end: '20:00',
    color: 'green'
  },

  // June 4th, 2026
  {
    id: 'e13',
    title: 'feriado',
    type: 'blocking',
    date: '2026-06-04',
    start: '09:00',
    end: '10:00',
    color: 'orange'
  },
  {
    id: 'e14',
    title: 'REIKI',
    type: 'compromise',
    date: '2026-06-04',
    start: '10:40',
    end: '11:40',
    color: 'orange'
  },
  {
    id: 'e15',
    title: 'feriado',
    type: 'blocking',
    date: '2026-06-04',
    start: '12:00',
    end: '13:00',
    color: 'orange'
  },

  // June 5th, 2026
  {
    id: 'e16',
    title: 'Paciente 427TCT724 - Bruno De Rossi',
    type: 'appointment',
    date: '2026-06-05',
    start: '10:00',
    end: '11:00',
    patientName: 'Bruno De Rossi',
    color: 'green'
  },
  {
    id: 'e17',
    title: 'Paciente 413TCT314 - Alexei Martchenko',
    type: 'appointment',
    date: '2026-06-05',
    start: '11:00',
    end: '12:00',
    patientName: 'Alexei Martchenko',
    color: 'green'
  },
  {
    id: 'e18',
    title: 'Paciente 434TCT434 - Duane Alessandro',
    type: 'appointment',
    date: '2026-06-05',
    start: '12:00',
    end: '13:00',
    patientName: 'Duane Alessandro',
    color: 'green'
  },
  {
    id: 'e19',
    title: 'Compromisso pessoal',
    type: 'compromise',
    date: '2026-06-05',
    start: '13:00',
    end: '14:00',
    color: 'orange'
  },
  {
    id: 'e19_extra1',
    title: 'Reunião Supervisão Geral',
    type: 'compromise',
    date: '2026-06-05',
    start: '15:00',
    end: '16:00',
    color: 'orange'
  },
  {
    id: 'e19_extra2',
    title: 'Fisioterapia',
    type: 'compromise',
    date: '2026-06-05',
    start: '16:30',
    end: '17:30',
    color: 'orange'
  },
  {
    id: 'e19_extra3',
    title: 'Sessão Pessoal de Terapia',
    type: 'compromise',
    date: '2026-06-05',
    start: '18:00',
    end: '19:00',
    color: 'orange'
  },

  // June 18th, 2026
  {
    id: 'e20',
    title: 'Aniversário | Paciente 252TCT',
    type: 'blocking',
    date: '2026-06-18',
    start: '00:00',
    end: '23:59',
    color: 'purple'
  }
];

interface AgendaProps {
  /**
   * Carteira de pacientes vinda da API. Quando ausente, cai no cache local
   * (`allodos-patients`) para não quebrar usos legados do componente.
   */
  patients?: Patient[];
  userProfiles?: any[];
  activeProfile?: any;
}

/**
 * Lança no livro caixa a entrada correspondente ao atendimento.
 * `attended = false` significa falta cobrada.
 */
const attendanceKind = (event: CalendarEvent): 'agenda' | 'appointment' => (event.source === 'portal' ? 'appointment' : 'agenda');

/** Valor do atendimento: o gravado no agendamento ou o preço atual do serviço (só para exibir). */
const attendanceValue = (event: CalendarEvent): number =>
  parseAmount(event.serviceValue || formatPrice(readServicesFromStorage().find((s) => s.name === event.serviceName)?.price) || '');

/**
 * Lança no Livro Caixa (banco) a entrada do atendimento; `attended = false`
 * é falta cobrada. A API usa o valor do serviço e não duplica (bloco #55).
 * Devolve o valor e se o lançamento é novo, ou null quando não há lançamento.
 */
async function registerAttendanceIncome(event: CalendarEvent, attended: boolean): Promise<{ amount: number; created: boolean } | null> {
  if (!isUuid(event.id)) return null;
  const r = await recordAttendanceIncome(attendanceKind(event), event.id, attended);
  return r.entry ? { amount: r.entry.amount, created: r.created } : null;
}

const DEFAULT_EVENT_IDS = new Set(DEFAULT_EVENTS.map((e) => e.id));

type CancelScope = 'one' | 'series';

type AgendaViewMode = 'month' | 'week' | 'day' | 'list';
const AGENDA_VIEW_MODES: AgendaViewMode[] = ['month', 'week', 'day', 'list'];

/** Bloco #74: o último modo de exibição fica guardado por usuário neste navegador. */
function viewModeKey(): string {
  try {
    const user = JSON.parse(localStorage.getItem('allodos-user') || 'null') as { id?: string } | null;
    return `allodos-agenda-view:${user?.id || 'anon'}`;
  } catch {
    return 'allodos-agenda-view:anon';
  }
}

function readViewMode(): AgendaViewMode {
  try {
    const saved = localStorage.getItem(viewModeKey()) as AgendaViewMode | null;
    return saved && AGENDA_VIEW_MODES.includes(saved) ? saved : 'month';
  } catch {
    return 'month';
  }
}

const Agenda: React.FC<AgendaProps> = ({ patients: patientsProp, userProfiles: userProfilesProp = [], activeProfile: activeProfileProp }) => {
  // O calendário abre sempre na data atual do dispositivo (mês e dia de hoje),
  // coerente com a data exibida no cabeçalho.
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [viewMode, setViewModeState] = useState<AgendaViewMode>(readViewMode);
  const setViewMode = (mode: AgendaViewMode) => {
    setViewModeState(mode);
    try { localStorage.setItem(viewModeKey(), mode); } catch { /* navegador sem storage: só não lembra */ }
  };
  
  // Os eventos vivem na API (é isso que faz a agenda online externa respeitar
  // bloqueios e sessões). O cache local só cobre a primeira pintura e o modo
  // offline; os eventos antigos que só existiam aqui são migrados uma vez.
  const legacyCacheRef = React.useRef<CalendarEvent[]>(readCachedEvents());
  const [events, setEvents] = useState<CalendarEvent[]>(() => legacyCacheRef.current);
  /** Bloco #56: conta Google do usuário conectada (a API cria a sala do Meet no "Acessar Sala"). */
  const [googleConnected, setGoogleConnected] = useState(false);
  useEffect(() => {
    getGoogleMeetStatus()
      .then((st) => setGoogleConnected(st.configured && st.connected && !st.error))
      .catch(() => setGoogleConnected(false));
  }, []);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  // Serviços cadastrados em Configurações da Agenda (para o "Tipo de Serviço").
  // Se este navegador ainda não abriu as configurações, usa os serviços da
  // página de agendamento publicada (a mesma lista que o paciente vê).
  const [services, setServices] = useState<AgendaService[]>(() => readServicesFromStorage());
  useEffect(() => {
    const reload = () => {
      const locais = readServicesFromStorage();
      if (locais.length) setServices(locais);
    };
    window.addEventListener('storage', reload);
    window.addEventListener('focus', reload);
    if (!readServicesFromStorage().length) {
      fetchMyBookingPage()
        .then((page) => {
          const publicados = (page?.config?.services ?? []).filter((s) => s && s.active !== false);
          if (publicados.length) {
            setServices(publicados as AgendaService[]);
            localStorage.setItem(scopedKey(SERVICES_STORAGE_KEY), JSON.stringify(publicados));
          }
        })
        .catch((err) => console.error('Falha ao carregar serviços publicados:', err));
    }
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('focus', reload);
    };
  }, []);

  // Bloco 44: locais de atendimento cadastrados em Configurações da Agenda,
  // para o "Local de Atendimento" do agendamento acompanhar a modalidade.
  const [locations, setLocations] = useState<AgendaLocation[]>(() => readLocationsFromStorage());
  useEffect(() => {
    const reload = () => {
      const locais = readLocationsFromStorage();
      if (locais.length) setLocations(locais);
    };
    window.addEventListener('storage', reload);
    window.addEventListener('focus', reload);
    if (!readLocationsFromStorage().length) {
      fetchMyBookingPage()
        .then((page) => {
          const publicados = (page?.config?.locations ?? []).filter((l) => l && l.active !== false);
          if (publicados.length) setLocations(publicados as AgendaLocation[]);
        })
        .catch((err) => console.error('Falha ao carregar locais publicados:', err));
    }
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('focus', reload);
    };
  }, []);

  // State for available patients loaded from localStorage
  const [patients, setPatients] = useState<Patient[]>([]);

  // State for all clinic user profiles
  const [profiles, setProfiles] = useState<any[]>(() => {
    if (Array.isArray(userProfilesProp) && userProfilesProp.length) return userProfilesProp;
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // State for active simulated profile
  const [activeProfile, setActiveProfile] = useState<any>(() => {
    if (activeProfileProp) return activeProfileProp;
    try {
      const profilesSaved = localStorage.getItem('allodos-user-profiles');
      const activeId = localStorage.getItem('allodos-active-profile-id') || '1';
      if (profilesSaved) {
        const list = JSON.parse(profilesSaved);
        return list.find((p: any) => p.id === activeId) || list[0];
      }
    } catch (e) {}
    return { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador / Psicóloga', email: 'contato@clinicaevoluir.com', status: 'Ativo' };
  });

  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string>(activeProfile?.id || '1');

  useEffect(() => {
    if (userProfilesProp.length) setProfiles(userProfilesProp);
  }, [userProfilesProp]);

  useEffect(() => {
    if (activeProfileProp) setActiveProfile(activeProfileProp);
  }, [activeProfileProp]);

  useEffect(() => {
    setSelectedProfessionalId(activeProfile?.id || '1');
  }, [activeProfile]);

  useEffect(() => {
    const handleProfileChange = () => {
      if (userProfilesProp.length || activeProfileProp) return;
      try {
        const profilesSaved = localStorage.getItem('allodos-user-profiles');
        const activeId = localStorage.getItem('allodos-active-profile-id') || '1';
        if (profilesSaved) {
          const list = JSON.parse(profilesSaved);
          setProfiles(list);
          const active = list.find((p: any) => p.id === activeId) || list[0];
          setActiveProfile(active);
        }
      } catch (e) {}
    };
    window.addEventListener('profile-switched', handleProfileChange);
    // Trigger initially to populate profiles and activeProfile
    handleProfileChange();
    return () => {
      window.removeEventListener('profile-switched', handleProfileChange);
    };
  }, [userProfilesProp.length, activeProfileProp]);

  const isProfessional = useMemo(() => {
    const roleLower = (activeProfile?.role || '').toLowerCase();
    return (roleLower.includes('profissional') || roleLower.includes('psicólog') || roleLower.includes('terapeut')) && !roleLower.includes('administrador');
  }, [activeProfile]);

  const targetProfId = useMemo(() => {
    return isProfessional ? activeProfile.id : selectedProfessionalId;
  }, [isProfessional, activeProfile, selectedProfessionalId]);

  const filteredPatientsForAppointment = useMemo(() => {
    if (isProfessional) {
      return patients.filter(p => p.assignedProfessionalId === activeProfile.id);
    }
    return patients;
  }, [patients, isProfessional, activeProfile]);

  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      // 1. Se o evento possui ID do profissional, valida correspondência exata
      if (e.professionalId) {
        return e.professionalId === targetProfId;
      }
      // 2. Se o evento possui ID de paciente, verifica se o paciente está associado ao profissional selecionado
      if (e.patientId) {
        const patientObj = patients.find(p => p.id === e.patientId);
        if (patientObj) {
          return patientObj.assignedProfessionalId === targetProfId;
        }
      }
      // 3. Se possui nome do paciente (caso de eventos padrão), verifica pelo nome se o paciente está associado ao profissional selecionado
      if (e.patientName) {
        const patientObj = patients.find(p => p.name.toLowerCase() === e.patientName.toLowerCase());
        if (patientObj) {
          return patientObj.assignedProfessionalId === targetProfId;
        }
      }
      // 4. Se não possui ID de profissional nem paciente (ex: compromissos pessoais e bloqueios padrão),
      // estes pertencem à Dra. Marina Lopes (ID '1') por padrão, para não poluir as outras agendas.
      return targetProfId === '1';
    });
  }, [events, targetProfId, patients]);

  // States for modals and interactive popups
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [modalType, setModalType] = useState<'appointment' | 'compromise' | 'blocking' | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [dayEventsPopoverDate, setDayEventsPopoverDate] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchPatient = () => {
    if (!searchTerm.trim()) return;
    
    const matchedEvents = filteredEvents.filter(e => 
      e.type === 'appointment' && 
      ((e.patientName && e.patientName.toLowerCase().includes(searchTerm.toLowerCase())) || 
       (e.title && e.title.toLowerCase().includes(searchTerm.toLowerCase())))
    );
    
    if (matchedEvents.length === 0) {
      setToastMessage({ text: `Nenhum agendamento encontrado para "${searchTerm}".`, type: 'info' });
      return;
    }
    
    matchedEvents.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.start || '00:00'}`);
      const dateB = new Date(`${b.date}T${b.start || '00:00'}`);
      return dateA.getTime() - dateB.getTime();
    });
    
    const referenceDate = new Date();
    const futureOrPresent = matchedEvents.find(e => {
      const eventDate = new Date(`${e.date}T${e.start || '00:00'}`);
      return eventDate >= referenceDate;
    });
    
    const targetEvent = futureOrPresent || matchedEvents[matchedEvents.length - 1];
    
    const targetDate = new Date(`${targetEvent.date}T12:00:00`);
    setCurrentDate(targetDate);
    setSelectedEvent(targetEvent);
    
    setToastMessage({ text: `Agendamento encontrado para ${targetEvent.patientName || targetEvent.title}!`, type: 'success' });
  };

  // New interactive states for selected event options
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showPatientProfileInline, setShowPatientProfileInline] = useState(false);
  const [showSessionNotesForm, setShowSessionNotesForm] = useState(false);
  const [sessionNotesInput, setSessionNotesInput] = useState('');

  // Toast auto-dismiss effect
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Form Fields State
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formStart, setFormStart] = useState('09:00');
  const [formEnd, setFormEnd] = useState('10:00');
  const [formPatientId, setFormPatientId] = useState('');
  const [formModality, setFormModality] = useState<'PRESENCIAL' | 'ONLINE' | 'PRESENCIAL_ONLINE'>('PRESENCIAL');
  const [formLocation, setFormLocation] = useState('Consultório Principal');

  /**
   * Bloco 44: locais compatíveis com a modalidade escolhida no formulário
   * (presencial → locais presenciais; online → locais online), só os ativos
   * e, quando o local restringe profissionais (bloco 36), só os do
   * profissional da agenda.
   */
  const locationsForModality = useMemo(() => {
    const tipo = formModality === 'ONLINE' ? 'online' : 'presential';
    return locations.filter((l) => {
      if (l.active === false || l.type !== tipo) return false;
      if (!targetProfId || !Array.isArray(l.professionalIds) || l.professionalIds.length === 0) return true;
      return l.professionalIds.includes(String(targetProfId));
    });
  }, [locations, formModality, targetProfId]);

  // Ao trocar a modalidade (ou carregar os locais), o local selecionado passa a
  // ser o primeiro compatível, sem recarregar a tela.
  useEffect(() => {
    if (modalType !== 'appointment') return;
    if (locationsForModality.length === 0) return;
    if (!locationsForModality.some((l) => l.name === formLocation)) {
      setFormLocation(locationsForModality[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsForModality, modalType]);

  const [formNotes, setFormNotes] = useState('');
  const [formRecurrence, setFormRecurrence] = useState<'NONE' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'>('NONE');
  const [formRecurrenceCount, setFormRecurrenceCount] = useState<number>(4);
  const [formServiceValue, setFormServiceValue] = useState<string>('150,00');
  const [formServiceId, setFormServiceId] = useState<string>('');
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [formNotifyPatient, setFormNotifyPatient] = useState<boolean>(false);
  const [formAllDay, setFormAllDay] = useState<boolean>(false);
  const [formIsPeriod, setFormIsPeriod] = useState<boolean>(false);
  const [formEndDate, setFormEndDate] = useState<string>('');
  const [overlappingEventsPending, setOverlappingEventsPending] = useState<{
    events: CalendarEvent[];
    generated: CalendarEvent[];
  } | null>(null);

  /** Persiste eventos novos na API e devolve-os com os IDs definitivos. */
  const persistNewEvents = async (generated: CalendarEvent[]): Promise<CalendarEvent[]> => {
    const created = await createAgendaEvents(generated.map(toAgendaPayload));
    return created.map(mapAgendaEvent);
  };

  /** Remove (agenda) ou cancela (portal) um evento na API; `notifyPatient` = bloco #53. */
  /** Devolve os ids que saíram da agenda (a série inteira, no "toda a recorrência"). */
  const removeEventRemotely = async (event: CalendarEvent, notifyPatient = false, scope: CancelScope = 'one'): Promise<string[]> => {
    if (event.source === 'portal') {
      await cancelAppointment(event.id, notifyPatient);
      return [event.id];
    }
    const r = await deleteAgendaEvent(event.id, notifyPatient, scope);
    return r.removedIds?.length ? r.removedIds : [event.id];
  };

  // Bloco #53: atendimento com paciente pergunta se avisa o paciente antes de cancelar.
  const [cancelPrompt, setCancelPrompt] = useState<CalendarEvent | null>(null);
  // Bloco #73: item com recorrência pergunta antes se cancela só o dia ou a série.
  const [scopePrompt, setScopePrompt] = useState<CalendarEvent | null>(null);
  const [cancelScope, setCancelScope] = useState<CancelScope>('one');
  const [isCancelling, setIsCancelling] = useState(false);
  /**
   * Bloco #57: "Acessar Sala" envia o link da sala ao paciente (WhatsApp e
   * e-mail) e abre a sala em nova aba. A aba abre já no clique (senão o
   * navegador bloqueia o pop-up) e recebe o endereço quando a API responde.
   */
  /** Bloco 57: link da sala do local online do atendimento (ou de um único local online com link). */
  const roomLinkFor = (event: CalendarEvent): string | null => {
    const online = locations.filter((l) => l.type === 'online' && l.active !== false && l.link?.trim());
    const named = online.find((l) => l.name === event.locationName);
    if (named) return named.link!.trim();
    const links = [...new Set(online.map((l) => l.link!.trim()))];
    return links.length === 1 ? links[0] : null;
  };

  const accessRoom = async (event: CalendarEvent) => {
    // Sem sala do Meet (#56) nem link no local, o paciente não seria avisado:
    // pergunta antes. Com o Google conectado, a API cria a sala na hora.
    if (!event.meetLink && !roomLinkFor(event) && !googleConnected && isUuid(event.id)) {
      const abrir = window.confirm(
        `Nenhuma sala virtual está vinculada a "${event.locationName || 'o local online'}". ` +
          'O paciente NÃO receberá o aviso por WhatsApp/e-mail.\n\n' +
          'Conecte sua conta Google na aba Teleconsulta (sala automática do Meet) ou cadastre o link em Agenda › Configurações da Agenda › Locais de Atendimento.\n\n' +
          'Abrir uma sala nova do Google Meet mesmo assim?',
      );
      if (!abrir) return;
    }
    const tab = window.open('', '_blank');
    const canal: Record<string, string> = {
      ENVIADO: 'enviado',
      JA_ENVIADO: 'já enviado antes',
      SEM_CONTATO: 'sem contato na ficha',
      DESLIGADO: 'aviso desligado',
      NAO_CONFIGURADO: 'envio não configurado',
      FALHOU: 'falhou',
    };
    let result: Awaited<ReturnType<typeof accessTeleconsulta>> | null = null;
    let apiError: string | null = null;
    try {
      if (isUuid(event.id)) result = await accessTeleconsulta(event.source === 'portal' ? 'appointment' : 'agenda', event.id);
      else apiError = 'Este agendamento antigo não está sincronizado com o servidor; recrie-o para avisar o paciente.';
    } catch (err) {
      console.error(err);
      apiError = describeError(err);
    }
    setSelectedEvent(null);
    if (apiError) {
      // Erro da API (ex.: agendamento sem paciente vinculado) não é "sem sala":
      // mostra o motivo real e ainda abre uma sala para o atendimento seguir.
      if (tab) tab.location.href = event.meetLink || roomLinkFor(event) || 'https://meet.google.com/new';
      setToastMessage({ text: `O paciente não foi avisado: ${apiError}`, type: 'error' });
      return;
    }
    if (result?.roomUrl) {
      const roomUrl = result.roomUrl;
      if (tab) tab.location.href = roomUrl;
      else window.open(roomUrl, '_blank');
      // #56: a sala criada agora passa a aparecer nos detalhes do agendamento.
      if (roomUrl !== roomLinkFor(event)) setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, meetLink: roomUrl } : e)));
      setToastMessage({
        text: `Sala aberta. Paciente: WhatsApp ${canal[result.whatsapp ?? ''] ?? '—'}, e-mail ${canal[result.email ?? ''] ?? '—'}.`,
        type: result.whatsapp === 'ENVIADO' || result.email === 'ENVIADO' || result.whatsapp === 'JA_ENVIADO' ? 'success' : 'info',
      });
      return;
    }
    // Sem sala do Meet (#56) nem link no local: abre uma sala nova do Meet, mas
    // o paciente não recebe link nenhum — avisa como vincular uma sala.
    if (tab) tab.location.href = 'https://meet.google.com/new';
    setToastMessage({
      text: 'Sem sala vinculada: o paciente não foi avisado. Conecte o Google na aba Teleconsulta ou cadastre o link em Configurações da Agenda › Locais.',
      type: 'error',
    });
  };

  const askCancel = (event: CalendarEvent) => {
    if (event.source !== 'portal' && event.recurrence && event.recurrence !== 'NONE') {
      setScopePrompt(event);
      return;
    }
    continueCancel(event, 'one');
  };

  const continueCancel = (event: CalendarEvent, scope: CancelScope) => {
    setScopePrompt(null);
    setCancelScope(scope);
    if (event.type === 'appointment' && (event.patientId || event.source === 'portal')) setCancelPrompt(event);
    else void handleDeleteEvent(event.id, false, scope);
  };

  const confirmSaveOverlap = async () => {
    if (!overlappingEventsPending) return;
    const pendentes = overlappingEventsPending;
    setIsSavingEvent(true);
    try {
      await Promise.all(pendentes.events.map((ev) => removeEventRemotely(ev)));
      const created = await persistNewEvents(pendentes.generated);
      const overlapIds = new Set(pendentes.events.map(e => e.id));
      setEvents(prev => [...prev.filter(e => !overlapIds.has(e.id)), ...created]);
      setOverlappingEventsPending(null);
      setModalType(null);
    } catch (err) {
      console.error(err);
      setToastMessage({ text: `Não foi possível salvar: ${describeError(err)}`, type: 'error' });
    } finally {
      setIsSavingEvent(false);
    }
  };

  // Bloco #54: a confirmação do paciente pelo WhatsApp (e o que outra pessoa
  // da equipe marcar) chega sem recarregar a página — a cor muda sozinha.
  useEffect(() => {
    if (!eventsLoaded) return;
    let ativo = true;
    const atualizar = async () => {
      if (document.hidden) return;
      try {
        const [internos, remotos] = await Promise.all([fetchAgendaEvents(), fetchAppointments()]);
        if (!ativo) return;
        const daApi = [
          ...(internos ?? []).filter((e) => e.patientResponse !== 'CANCELADO').map(mapAgendaEvent),
          ...(remotos ?? []).filter((a: AppointmentResponse) => a.status !== 'CANCELADO').map(mapAppointment),
        ];
        // Eventos antigos que só existem neste navegador continuam na tela.
        setEvents((prev) => [...daApi, ...prev.filter((e) => !isUuid(e.id))]);
      } catch {
        /* sem rede: mantém o que está na tela */
      }
    };
    const timer = window.setInterval(atualizar, 30_000);
    window.addEventListener('focus', atualizar);
    return () => {
      ativo = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', atualizar);
    };
  }, [eventsLoaded]);

  // Cache local (primeira pintura / offline). Só depois da carga da API, para
  // não sobrescrever os eventos antigos antes da migração.
  useEffect(() => {
    if (!eventsLoaded) return;
    localStorage.setItem(scopedKey(CALENDAR_CACHE_KEY), JSON.stringify(events));
  }, [events, eventsLoaded]);

  /**
   * Carrega a agenda da API: eventos internos (atendimentos, compromissos e
   * bloqueios) e as reservas feitas pelos pacientes no portal público.
   * Sem isso, a reserva ficava só no banco e o profissional nunca a via — e
   * os bloqueios ficavam só neste navegador, invisíveis para o link externo.
   */
  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      try {
        const [internos, remotos] = await Promise.all([fetchAgendaEvents(), fetchAppointments()]);
        if (cancelado) return;
        // Bloco #51: atendimento cancelado pelo paciente (link do WhatsApp) sai
        // da grade e libera o horário — não fica riscado ocupando o espaço.
        const daAgenda = (internos ?? []).filter((e) => e.patientResponse !== 'CANCELADO').map(mapAgendaEvent);
        const doPortal = (remotos ?? [])
          .filter((a: AppointmentResponse) => a.status !== 'CANCELADO')
          .map(mapAppointment);
        setEvents([...daAgenda, ...doPortal]);
        setEventsLoaded(true);

        // Migração única dos eventos que só existiam no localStorage.
        if (!localStorage.getItem(CALENDAR_MIGRATED_KEY)) {
          const jaNaApi = new Set(daAgenda.map((e) => e.id));
          const legados = legacyCacheRef.current.filter(
            (e) => !DEFAULT_EVENT_IDS.has(e.id) && !isUuid(e.id) && !jaNaApi.has(e.id) && e.date && e.start,
          );
          if (legados.length) {
            const migrados = await persistNewEvents(legados);
            if (cancelado) return;
            setEvents((prev) => [...prev, ...migrados]);
          }
          localStorage.setItem(CALENDAR_MIGRATED_KEY, new Date().toISOString());
        }
      } catch (err) {
        console.error('Falha ao carregar a agenda:', err);
        if (!cancelado) {
          setToastMessage({ text: 'Não foi possível carregar a agenda da API. Exibindo cache local.', type: 'error' });
        }
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  // Carrega a lista de pacientes: prioriza os dados da API (via prop) e
  // só recorre ao cache local quando o componente é usado sem ela.
  useEffect(() => {
    if (patientsProp) {
      setPatients(selectablePatients(patientsProp));
      return;
    }
    try {
      const saved = localStorage.getItem('allodos-patients');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setPatients(selectablePatients(parsed));
        }
      }
    } catch (e) {
      console.error('Error loading patients', e);
    }
  }, [patientsProp]);

  // Listen to agenda action dispatch from header
  useEffect(() => {
    const handleAgendaAction = (e: Event) => {
      const customEvent = e as CustomEvent<'appointment' | 'compromise' | 'blocking'>;
      if (customEvent.detail) {
        openCreateModal(customEvent.detail);
      }
    };
    window.addEventListener('agenda-action', handleAgendaAction);
    return () => {
      window.removeEventListener('agenda-action', handleAgendaAction);
    };
    // `services` entra nas dependências para o formulário abrir com o tipo
    // de serviço e o valor já preenchidos (sem closure antiga vazia).
  }, [currentDate, services]);

  const monthNames = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const weekdayNames = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.'];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === 'month' || viewMode === 'list') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };

  const handleNext = () => {
    if (viewMode === 'month' || viewMode === 'list') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  /** Serviço escolhido no formulário (ou o primeiro cadastrado). */
  const selectService = (serviceId: string) => {
    setFormServiceId(serviceId);
    const svc = services.find((s) => s.id === serviceId);
    if (svc) setFormServiceValue(formatPrice(svc.price) || '');
  };

  // Month days calculation
  const monthGridDays = useMemo(() => {
    const totalDaysInPrevMonth = new Date(year, month, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDaysInCurrMonth = new Date(year, month + 1, 0).getDate();

    const daysList: { date: Date; isCurrentMonth: boolean; key: string }[] = [];

    // Trailing days from previous month
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = totalDaysInPrevMonth - i;
      const d = new Date(year, month - 1, dayNum);
      daysList.push({
        date: d,
        isCurrentMonth: false,
        key: `prev-${dayNum}`
      });
    }

    // Days of current month
    for (let i = 1; i <= totalDaysInCurrMonth; i++) {
      const d = new Date(year, month, i);
      daysList.push({
        date: d,
        isCurrentMonth: true,
        key: `curr-${i}`
      });
    }

    // Leading days of next month
    const remainingCells = 42 - daysList.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingCells; i++) {
      const d = new Date(year, month + 1, i);
      daysList.push({
        date: d,
        isCurrentMonth: false,
        key: `next-${i}`
      });
    }

    return daysList;
  }, [year, month]);

  // Format Date Helper (YYYY-MM-DD)
  const formatDateString = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Filter events for a specific date
  const getEventsForDate = (dateStr: string) => {
    return filteredEvents.filter(e => e.date === dateStr).sort((a, b) => a.start.localeCompare(b.start));
  };

  // Open Form Dialog
  const openCreateModal = (type: 'appointment' | 'compromise' | 'blocking', targetDate?: Date) => {
    const activeDate = targetDate || currentDate;
    setModalType(type);
    setFormTitle('');
    setFormDate(formatDateString(activeDate));
    setFormStart('09:00');
    setFormEnd('10:00');
    setFormPatientId('');
    setFormModality('PRESENCIAL');
    setFormLocation('Consultório Principal');
    setFormNotes('');
    setFormRecurrence('NONE');
    setFormRecurrenceCount(4);
    // Tipo de serviço: o primeiro cadastrado já vem selecionado e o valor
    // acompanha o pré-configurado (continua editável).
    const primeiro = services[0];
    setFormServiceId(primeiro?.id || '');
    setFormServiceValue(primeiro ? formatPrice(primeiro.price) : '150,00');
    setFormNotifyPatient(false);
    setFormAllDay(false);
    setFormIsPeriod(false);
    setFormEndDate(formatDateString(activeDate));
    setOverlappingEventsPending(null);
    setShowFloatingMenu(false);
  };

  // Open Edit Dialog for existing event
  const openEditModal = (event: CalendarEvent) => {
    setSelectedEvent(null); // Close details modal
    setEditingEventId(event.id);
    setModalType(event.type);
    
    // Extract patient name out of title if necessary
    const rawTitle = event.title.split('|')[0].trim();
    setFormTitle(rawTitle);
    setFormDate(event.date);
    setFormStart(event.start || '09:00');
    setFormEnd(event.end || '10:00');
    setFormPatientId(event.patientId || '');
    setFormModality(event.modality || 'PRESENCIAL');
    setFormLocation(event.locationName || 'Consultório Principal');
    setFormNotes(event.notes || '');
    setFormRecurrence('NONE');
    setFormRecurrenceCount(4);
    setFormServiceValue(event.serviceValue || '150,00');
    setFormServiceId(services.find((s) => s.name === event.serviceName)?.id || '');
    setFormNotifyPatient(event.notifyPatient || false);
    setFormAllDay(event.allDay || false);
    setFormIsPeriod(false);
    setFormEndDate(event.date);
    setOverlappingEventsPending(null);
    setShowFloatingMenu(false);
  };

  // Submit new event
  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDate || isSavingEvent) return;

    const selectedService = services.find((s) => s.id === formServiceId);
    const serviceLabel = selectedService?.name || 'Psicoterapia';

    if (editingEventId) {
      const existente = events.find((evt) => evt.id === editingEventId);
      if (!existente) return;
      const matchedPatient = patients.find(p => p.id === formPatientId);
      const atualizado: CalendarEvent = {
        ...existente,
        title: modalType === 'appointment' ? (matchedPatient ? `${matchedPatient.name} | ${serviceLabel}` : formTitle || 'Atendimento') : formTitle,
        date: formDate,
        start: (formAllDay || formIsPeriod) ? '00:00' : formStart,
        end: (formAllDay || formIsPeriod) ? '23:59' : formEnd,
        patientId: modalType === 'appointment' ? (formPatientId || existente.patientId) : undefined,
        patientName: modalType === 'appointment' ? (matchedPatient?.name || existente.patientName) : undefined,
        modality: modalType === 'appointment' ? formModality : undefined,
        locationName: modalType === 'appointment' ? formLocation : undefined,
        notes: formNotes || undefined,
        color: existente.source === 'portal' ? existente.color : colorFor(modalType!),
        serviceValue: modalType === 'appointment' ? formServiceValue : undefined,
        serviceName: modalType === 'appointment' ? selectedService?.name : undefined,
        allDay: formAllDay || formIsPeriod,
        professionalId: existente.professionalId || targetProfId
      };

      setIsSavingEvent(true);
      try {
        if (existente.source === 'portal') {
          await updateAppointment(existente.id, {
            date: atualizado.date,
            start: atualizado.start,
            end: atualizado.end,
            notes: atualizado.notes ?? '',
            modality: atualizado.modality,
            locationName: atualizado.locationName ?? '',
            serviceName: atualizado.serviceName ?? '',
            patientId: atualizado.patientId ?? '',
          });
        } else {
          await updateAgendaEvent(existente.id, toAgendaPayload(atualizado));
        }
      } catch (err) {
        console.error(err);
        setToastMessage({ text: `Não foi possível atualizar: ${describeError(err)}`, type: 'error' });
        setIsSavingEvent(false);
        return;
      }
      setIsSavingEvent(false);
      setEvents(prev => prev.map(evt => (evt.id === editingEventId ? atualizado : evt)));

      // Notify patient if selected
      if (modalType === 'appointment' && formNotifyPatient) {
        const selectedPatient = patients.find(p => p.id === formPatientId);
        const phone = selectedPatient?.phone || '';
        const patientName = selectedPatient?.name || 'Paciente';
        const dateParts = formDate.split('-');
        const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
        const text = `Olá, ${patientName}! Seu agendamento foi alterado para o dia ${formattedDate} às ${formStart}. Até breve!`;
        
        let cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone && !cleanPhone.startsWith('55') && cleanPhone.length <= 11) {
          cleanPhone = '55' + cleanPhone;
        }
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const whatsappUrl = isMobile 
          ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(text)}`
          : `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, '_blank');
      }

      setEditingEventId(null);
      setModalType(null);
      setToastMessage({ text: 'Agendamento atualizado com sucesso!', type: 'success' });
      return;
    }

    let finalTitle = formTitle;
    let finalPatientName = '';

    if (modalType === 'appointment') {
      const selectedPatient = patients.find(p => p.id === formPatientId);
      if (selectedPatient) {
        finalPatientName = selectedPatient.name;
        finalTitle = `${selectedPatient.name} | ${serviceLabel}`;
      } else if (!finalTitle) {
        finalTitle = 'Novo Atendimento';
      }
    } else if (!finalTitle) {
      finalTitle = modalType === 'compromise' ? 'Compromisso' : 'Bloqueio de Agenda';
    }

    const generatedEvents: CalendarEvent[] = [];
    const datesToGenerate: string[] = [];

    if (modalType === 'compromise' && formIsPeriod && formEndDate) {
      const start = new Date(formDate + 'T12:00:00');
      const end = new Date(formEndDate + 'T12:00:00');
      const current = new Date(start);
      let limit = 0;
      while (current <= end && limit < 365) {
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        datesToGenerate.push(`${y}-${m}-${d}`);
        current.setDate(current.getDate() + 1);
        limit++;
      }
    } else {
      const count = ((modalType === 'appointment' || modalType === 'compromise') && formRecurrence !== 'NONE') ? formRecurrenceCount : 1;
      for (let i = 0; i < count; i++) {
        let eventDateStr = formDate;
        if (i > 0) {
          const baseDate = new Date(formDate + 'T12:00:00');
          if (formRecurrence === 'WEEKLY') {
            baseDate.setDate(baseDate.getDate() + (7 * i));
          } else if (formRecurrence === 'BIWEEKLY') {
            baseDate.setDate(baseDate.getDate() + (14 * i));
          } else if (formRecurrence === 'MONTHLY') {
            baseDate.setMonth(baseDate.getMonth() + i);
          }
          const y = baseDate.getFullYear();
          const m = String(baseDate.getMonth() + 1).padStart(2, '0');
          const d = String(baseDate.getDate()).padStart(2, '0');
          eventDateStr = `${y}-${m}-${d}`;
        }
        datesToGenerate.push(eventDateStr);
      }
    }

    for (let i = 0; i < datesToGenerate.length; i++) {
      const eventDateStr = datesToGenerate[i];
      const newEvent: CalendarEvent = {
        id: (Date.now() + i).toString(),
        title: finalTitle,
        type: modalType!,
        date: eventDateStr,
        start: (formAllDay || formIsPeriod) ? '00:00' : formStart,
        end: (formAllDay || formIsPeriod) ? '23:59' : formEnd,
        allDay: formAllDay || formIsPeriod,
        patientId: formPatientId || undefined,
        patientName: finalPatientName || undefined,
        modality: modalType === 'appointment' ? formModality : undefined,
        locationName: modalType === 'appointment' ? formLocation : undefined,
        notes: formNotes || undefined,
        color: modalType === 'appointment' ? 'green' : modalType === 'compromise' ? 'orange' : 'purple',
        serviceValue: modalType === 'appointment' ? formServiceValue : undefined,
        serviceName: modalType === 'appointment' ? selectedService?.name : undefined,
        source: 'agenda',
        recurrence: (modalType === 'appointment' || modalType === 'compromise') && formRecurrence !== 'NONE' ? formRecurrence : undefined,
        recurrenceCount: (modalType === 'appointment' || modalType === 'compromise') && formRecurrence !== 'NONE' ? formRecurrenceCount : undefined,
        notifyPatient: modalType === 'appointment' ? formNotifyPatient : undefined,
        professionalId: targetProfId
      };
      generatedEvents.push(newEvent);
    }

    // Check overlaps if saving a compromise
    if (modalType === 'compromise') {
      const overlappingAppointments = filteredEvents.filter(event => {
        if (event.type !== 'appointment') return false;
        if (!datesToGenerate.includes(event.date)) return false;
        if (formAllDay || formIsPeriod || event.allDay) return true;
        return event.start < formEnd && event.end > formStart;
      });

      if (overlappingAppointments.length > 0) {
        setOverlappingEventsPending({
          events: overlappingAppointments,
          generated: generatedEvents
        });
        return;
      }
    }

    // Persistir na API é o que faz o bloqueio/sessão valer no link externo
    // em tempo real.
    setIsSavingEvent(true);
    try {
      const created = await persistNewEvents(generatedEvents);
      setEvents(prev => [...prev, ...created]);
    } catch (err) {
      console.error(err);
      setToastMessage({ text: `Não foi possível salvar na agenda: ${describeError(err)}`, type: 'error' });
      setIsSavingEvent(false);
      return;
    }
    setIsSavingEvent(false);

    // "Notificar paciente": a API envia o WhatsApp automático com os links
    // Confirmar/Cancelar/Reagendar (bloco #42); nada a abrir aqui.
    if (modalType === 'appointment' && formNotifyPatient) {
      setToastMessage({ text: 'Agendamento salvo. O paciente será avisado por WhatsApp.', type: 'success' });
    }

    setModalType(null);
  };

  // Delete event (cancelar/excluir também libera o horário no link externo)
  const handleDeleteEvent = async (id: string, notifyPatient = false, scope: CancelScope = 'one') => {
    const alvo = events.find(e => e.id === id);
    let removidos = [id];
    if (alvo) {
      try {
        removidos = await removeEventRemotely(alvo, notifyPatient, scope);
      } catch (err) {
        console.error(err);
        setToastMessage({ text: `Não foi possível cancelar: ${describeError(err)}`, type: 'error' });
        return;
      }
    }
    // Bloco #55: a API tira o lançamento do Livro Caixa ao cancelar/excluir.
    const saiu = new Set(removidos);
    setEvents(prev => prev.filter(e => !saiu.has(e.id)));
    setSelectedEvent(null);
    const oQue = scope === 'series' ? `Recorrência cancelada (${removidos.length} ${removidos.length === 1 ? 'data' : 'datas'})` : 'Agendamento cancelado';
    setToastMessage({ text: notifyPatient ? `${oQue}. O paciente será avisado por WhatsApp.` : `${oQue}.`, type: 'success' });
  };

  /** Registra o comparecimento na API (agenda interna ou reserva do portal). */
  const persistAttendance = (event: CalendarEvent, attendance: 'Compareceu' | 'Não Compareceu') => {
    const req = event.source === 'portal'
      ? updateAppointment(event.id, { attendance })
      : updateAgendaEvent(event.id, { attendance });
    req.catch((err) => {
      console.error(err);
      setToastMessage({ text: `Comparecimento não sincronizado: ${describeError(err)}`, type: 'error' });
    });
  };

  // Get week date objects
  const weekDays = useMemo(() => {
    const currentDayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDayOfWeek);

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
  }, [currentDate]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300 relative select-none pb-20">
      
      {/* Dynamic Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-4 left-4 right-4 sm:bottom-auto sm:left-auto sm:top-6 sm:right-6 z-[100] bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-5 py-3.5 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-800/40 dark:border-slate-200/40 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-bold">{toastMessage.text}</span>
        </div>
      )}
      
      {/* HEADER SECTION - Styled in Allodos Layout */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm">
        
        {/* Navigation Arrows & Name */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <button 
              onClick={handlePrev} 
              className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition-all cursor-pointer border border-slate-200/50 dark:border-slate-800"
            >
              <ChevronLeft size={16} />
            </button>
            <button 
              onClick={handleNext} 
              className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition-all cursor-pointer border border-slate-200/50 dark:border-slate-800"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <button 
            onClick={handleToday}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-800 dark:text-slate-100 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer border border-slate-200/50 dark:border-slate-800"
          >
            Hoje
          </button>

          <h3 className="text-base sm:text-lg font-black text-slate-800 dark:text-white tracking-tight ml-1 sm:ml-2">
            {monthNames[month].charAt(0).toUpperCase() + monthNames[month].slice(1)} de {year}
          </h3>
        </div>

        {/* View selection tabs */}
        <div className="flex flex-col items-center gap-1.5 self-start md:self-auto w-full md:w-auto">
          <div className="flex flex-wrap justify-center items-center bg-slate-50 dark:bg-slate-950 p-1.5 rounded-2xl border border-slate-100 dark:border-slate-900 gap-1.5 sm:gap-2 w-full md:w-auto">
            {/* Campo de Busca de Paciente */}
            <div className="relative flex items-center bg-white dark:bg-slate-900 rounded-xl px-3 py-1.5 border border-slate-200/50 dark:border-slate-800 ml-1">
              <Search 
                size={12} 
                className="text-slate-400 cursor-pointer hover:text-indigo-600 transition-colors"
                onClick={handleSearchPatient}
              />
              <input
                type="text"
                placeholder="buscar paciente"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchPatient();
                  }
                }}
                className="w-24 sm:w-28 bg-transparent text-[10px] font-bold text-slate-700 dark:text-slate-300 placeholder-slate-400 outline-none ml-1.5 border-none p-0 focus:ring-0 focus:outline-none"
              />
            </div>

            {(['month', 'week', 'day', 'list'] as const).map((view) => (
              <button
                key={view}
                onClick={() => {
                  setViewMode(view);
                  setDayEventsPopoverDate(null);
                }}
                className={`px-3 sm:px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
                  viewMode === view 
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/15' 
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {view === 'month' ? 'Mês' : view === 'week' ? 'Semana' : view === 'day' ? 'Dia' : 'Vertical'}
              </button>
            ))}
          </div>
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
            Modos de Exibição
          </span>
        </div>

        {/* Selector of professionals */}
        {(() => {
          const showProfessionalSelect = activeProfile && (() => {
            const roleLower = (activeProfile.role || '').toLowerCase();
            return roleLower.includes('administrador') || roleLower.includes('recep') || roleLower.includes('secretar');
          })();

          const professionalsList = (() => {
            const list = profiles.filter((p: any) => {
              const roleLower = (p.role || '').toLowerCase();
              return !roleLower.includes('recep') && !roleLower.includes('secretar');
            });
            const baseList = list.length === 0 && activeProfile ? [activeProfile] : list;
            return [...baseList].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
          })();

          return showProfessionalSelect ? (
            <div className="flex flex-col items-start md:items-end gap-1.5 self-start md:self-auto w-full md:w-auto md:min-w-[200px]">
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Profissional da Agenda
              </span>
              <div className="relative w-full">
                <select
                  value={selectedProfessionalId}
                  onChange={(e) => setSelectedProfessionalId(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 text-[10px] font-black uppercase tracking-widest py-2.5 px-4 pr-8 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer appearance-none"
                >
                  {professionalsList.map((prof) => (
                    <option key={prof.id} value={prof.id} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                      {prof.name} ({prof.role})
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500">
                  <svg className="fill-current h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden md:block w-[82px]" />
          );
        })()}
      </div>

      {/* CALENDAR BODY */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm overflow-hidden p-3 sm:p-8">
        
        {/* VIEW 1: MONTHLY VIEW */}
        {viewMode === 'month' && (
          <div className="space-y-4">
            {/* Weekdays row */}
            <div className="grid grid-cols-7 gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              {weekdayNames.map(day => (
                <div key={day} className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Month Grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 auto-rows-[84px] sm:auto-rows-[120px]">
              {monthGridDays.map(({ date, isCurrentMonth, key }) => {
                const dateStr = formatDateString(date);
                const dayEvents = getEventsForDate(dateStr);
                const isSelectedDay = formatDateString(currentDate) === dateStr;

                return (
                  <div
                    key={key}
                    onClick={() => {
                      setCurrentDate(date);
                      openCreateModal('appointment', date);
                    }}
                    className={`relative p-1 sm:p-3 rounded-xl sm:rounded-2xl border transition-all flex flex-col group cursor-pointer ${
                      isSelectedDay 
                        ? 'bg-indigo-50/40 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900/60' 
                        : isCurrentMonth
                          ? 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800/80 hover:border-slate-200 dark:hover:border-slate-700'
                          : 'bg-slate-50/50 dark:bg-slate-950/10 border-slate-100/50 dark:border-slate-800/30 opacity-40'
                    }`}
                  >
                    {/* Day number header */}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-black rounded-lg w-6 h-6 flex items-center justify-center transition-all ${
                        isSelectedDay 
                          ? 'bg-indigo-600 text-white shadow-sm' 
                          : 'text-slate-700 dark:text-slate-300'
                      }`}>
                        {date.getDate()}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                      )}
                    </div>

                    {/* Mini event listing */}
                    <div className="flex-1 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                          }}
                          className={`text-[9.5px] font-bold px-1 sm:px-2 py-1 rounded-lg truncate border shadow-sm transition-all ${eventTone(event).card}`}
                          title={`${event.title}${event.patientResponse === 'CANCELADO' ? ' (cancelado pelo paciente)' : event.patientResponse === 'CONFIRMADO' ? ' (paciente confirmou)' : ''}`}
                        >
                          <span className="font-extrabold text-[8px] mr-1 opacity-90">{event.allDay ? 'Dia Todo' : event.start}</span>
                          {event.patientResponse === 'CONFIRMADO' && <span aria-label="Paciente confirmou" className="mr-1">✓</span>}
                          <span className={`hidden sm:inline ${event.patientResponse === 'CANCELADO' ? 'line-through opacity-80' : ''}`}>{event.title.split('|')[0].trim()}</span>
                        </div>
                      ))}

                      {dayEvents.length > 3 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentDate(date);
                            setDayEventsPopoverDate(dateStr);
                          }}
                          className="w-full text-left text-[8px] font-black uppercase text-indigo-600 dark:text-indigo-400 hover:underline pl-2 py-0.5"
                        >
                          mais +{dayEvents.length - 3}
                        </button>
                      )}
                    </div>

                    {/* Popover overlay when clicked on "+ more" for that day */}
                    {dayEventsPopoverDate === dateStr && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setDayEventsPopoverDate(null); }}></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:translate-x-0 md:translate-y-0 md:top-8 md:left-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-[2rem] shadow-2xl z-20 w-64 max-h-72 overflow-y-auto space-y-2 text-left" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-2 mb-2">
                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Compromissos dia {date.getDate()}</span>
                            <button onClick={() => setDayEventsPopoverDate(null)} className="p-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-400"><X size={12} /></button>
                          </div>
                          <div className="space-y-1.5">
                            {dayEvents.map(event => (
                              <div
                                key={event.id}
                                onClick={() => {
                                  setSelectedEvent(event);
                                  setDayEventsPopoverDate(null);
                                }}
                                className={`text-[10.5px] font-bold p-2.5 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] shadow-sm ${eventTone(event).card}`}
                              >
                                <div className="flex justify-between font-extrabold text-[9px] opacity-90 mb-0.5">
                                  <span>{event.allDay ? 'Dia Inteiro' : `${event.start} - ${event.end}`}</span>
                                  <span className="uppercase">{event.type}</span>
                                </div>
                                <div className="truncate font-semibold">
                                  {event.patientResponse === 'CONFIRMADO' && <span aria-label="Paciente confirmou" className="mr-1">✓</span>}
                                  <span className={`hidden sm:inline ${event.patientResponse === 'CANCELADO' ? 'line-through opacity-80' : ''}`}>{event.title.split('|')[0].trim()}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* VIEW 2: WEEKLY VIEW */}
        {viewMode === 'week' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-7 gap-3 sm:gap-4">
              {weekDays.map(day => {
                const dateStr = formatDateString(day);
                const dayEvents = getEventsForDate(dateStr);
                const isSelected = formatDateString(currentDate) === dateStr;

                return (
                  <div 
                    key={dateStr} 
                    onClick={() => {
                      setCurrentDate(day);
                      openCreateModal('appointment', day);
                    }}
                    className={`space-y-4 p-4 rounded-[2rem] border sm:min-h-[350px] cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-indigo-50/20 border-indigo-200 dark:bg-indigo-950/10 dark:border-indigo-900/40' 
                        : 'bg-slate-50/40 dark:bg-slate-950/10 border-slate-100 dark:border-slate-800/60 hover:border-slate-200 dark:hover:border-slate-700'
                    }`}
                  >
                    {/* Day description */}
                    <div className="text-center pb-2 border-b border-slate-100 dark:border-slate-800">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        {weekdayNames[day.getDay()]}
                      </p>
                      <p className={`text-lg font-black mt-1 inline-flex items-center justify-center w-8 h-8 rounded-xl ${
                        isSelected ? 'bg-indigo-600 text-white' : 'text-slate-800 dark:text-slate-100'
                      }`}>
                        {day.getDate()}
                      </p>
                    </div>

                    {/* Events list of day */}
                    <div className="space-y-2">
                      {dayEvents.map(event => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                          }}
                          className={`p-3 rounded-2xl border text-xs font-bold space-y-1.5 cursor-pointer hover:scale-[1.02] transition-all shadow-sm ${eventTone(event).card}`}
                        >
                          <div className="flex justify-between text-[9px] font-bold opacity-90">
                            {event.allDay ? (
                              <span className="flex items-center gap-1">Dia Inteiro</span>
                            ) : (
                              <>
                                <span className="flex items-center gap-1"><Clock size={8} /> {event.start}</span>
                                <span>{event.end}</span>
                              </>
                            )}
                          </div>
                          <p className="leading-tight line-clamp-2 font-semibold">{event.title.split('|')[0]}</p>
                        </div>
                      ))}

                      {dayEvents.length === 0 && (
                        <div className="text-center py-10">
                          <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-600">Sem agendamentos</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* VIEW 3: DAY TIMELINE VIEW */}
        {viewMode === 'day' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-950 p-4 rounded-3xl border border-slate-100 dark:border-slate-900 mb-2">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex flex-col items-center justify-center shadow-md">
                <span className="text-sm font-black leading-none">{currentDate.getDate()}</span>
                <span className="text-[8px] font-black uppercase tracking-wider">{weekdayNames[currentDate.getDay()]}</span>
              </div>
              <div>
                <h4 className="font-black text-slate-800 dark:text-slate-100 text-sm">
                  {weekdayNames[currentDate.getDay()].charAt(0).toUpperCase() + weekdayNames[currentDate.getDay()].slice(1)} - {currentDate.getDate()} de {monthNames[month]} de {year}
                </h4>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Cronograma Diário</p>
              </div>
            </div>

            <div 
              onClick={() => openCreateModal('appointment', currentDate)}
              className="relative border-l-2 border-slate-100 dark:border-slate-800 ml-2 pl-6 sm:ml-4 sm:pl-8 space-y-6 py-4 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-900 transition-colors"
            >
              {getEventsForDate(formatDateString(currentDate)).map(event => (
                <div 
                  key={event.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEvent(event);
                  }}
                  className={`relative p-5 rounded-[2rem] border cursor-pointer hover:scale-[1.01] transition-all space-y-3 shadow-md ${eventTone(event).card}`}
                >
                  {/* Bullet indicator on vertical line */}
                  <div className={`absolute -left-[35px] sm:-left-[41px] top-1/2 -translate-y-1/2 w-4.5 h-4.5 rounded-full border-4 border-white dark:border-slate-900 ${eventTone(event).dot}`} />

                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 bg-white/20 text-white rounded-lg">
                        {event.type === 'appointment' ? 'Atendimento' : event.type === 'compromise' ? 'Compromisso' : 'Bloqueio'}
                      </span>
                      {event.modality && (
                        <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 bg-white/30 text-white rounded-lg">
                          {event.modality}
                        </span>
                      )}
                      {event.patientResponse && (
                        <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 bg-white text-slate-800 rounded-lg">
                          {event.patientResponse === 'CONFIRMADO' ? 'Paciente confirmou' : 'Cancelado pelo paciente'}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-black flex items-center gap-1.5 opacity-90">
                      {event.allDay ? 'Dia Inteiro' : <><Clock size={12} /> {event.start} - {event.end}</>}
                    </span>
                  </div>

                  <h3 className="text-base font-black tracking-tight truncate" title={event.title.split('|')[0].trim()}>{event.title.split('|')[0].trim()}</h3>

                  {event.locationName && (
                    <p className="text-xs font-bold flex items-center gap-1.5 opacity-90">
                      <MapPin size={12} /> {event.locationName}
                    </p>
                  )}

                  {event.notes && (
                    <p className="text-xs font-medium italic opacity-95 border-t border-white/15 pt-2.5">
                      "{event.notes}"
                    </p>
                  )}
                </div>
              ))}

              {getEventsForDate(formatDateString(currentDate)).length === 0 && (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateModal('appointment', currentDate);
                  }}
                  className="text-center py-10 sm:py-16 bg-slate-50/50 dark:bg-slate-950/10 border border-dashed border-slate-200 dark:border-slate-850 rounded-[2rem] cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-800 transition-all"
                >
                  <p className="text-sm font-bold text-slate-400 dark:text-slate-500">Nenhum compromisso ou atendimento programado para este dia.</p>
                  <p className="text-xs text-indigo-500 font-bold mt-1.5">Clique para agendar um novo atendimento</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* VIEW 4: LIST VIEW */}
        {viewMode === 'list' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="border-b border-slate-150 dark:border-slate-800 pb-4 flex justify-between items-center">
              <div>
                <h4 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                  Lista de Sessões e Compromissos
                </h4>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-1">
                  Exibição cronológica das atividades
                </p>
              </div>
              <span className="px-4 py-1.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest rounded-xl">
                {filteredEvents.length} Registros
              </span>
            </div>

            <div className="space-y-2">
              {filteredEvents
                .sort((a, b) => {
                  const dateCompare = a.date.localeCompare(b.date);
                  if (dateCompare !== 0) return dateCompare;
                  return a.start.localeCompare(b.start);
                })
                .map((event) => {
                  const eventDate = new Date(event.date + 'T12:00:00');
                  const weekday = eventDate.toLocaleDateString('pt-BR', { weekday: 'long' });
                  const formattedDate = eventDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
                  
                  // Clean up the lowercase "de" for the weekday & date display
                  const displayDateString = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${formattedDate}`;

                  return (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`py-2.5 px-6 rounded-2xl border transition-all cursor-pointer hover:scale-[1.01] shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-3 ${eventTone(event).card}`}
                    >
                      <div className="space-y-1 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[8px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 bg-white/20 text-white rounded">
                            {event.type === 'appointment' ? 'Atendimento Clínico' : event.type === 'compromise' ? 'Compromisso' : 'Bloqueio'}
                          </span>
                          {event.modality && (
                            <span className="text-[8px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 bg-white/30 text-white rounded">
                              {event.modality}
                            </span>
                          )}
                        </div>

                        <h3 className="text-sm font-black tracking-tight truncate" title={event.title.split('|')[0].trim()}>{event.title.split('|')[0].trim()}</h3>

                        {/* Modality specifics or online message */}
                        {event.type === 'appointment' && event.modality === 'ONLINE' ? (
                          <p className="text-[10px] font-medium leading-normal bg-white/10 py-1 px-3 rounded-lg border border-white/10 max-w-xl text-white/90">
                            Para um bom atendimento, garanta uma boa conexão de internet e privacidade. Certifique-se acerca das permissões de câmera e microfone.
                          </p>
                        ) : event.locationName ? (
                          <p className="text-[11px] font-bold flex items-center gap-1 text-white/80">
                            <MapPin size={11} className="text-white/75 shrink-0" />
                            {event.locationName}
                          </p>
                        ) : null}

                        {event.notes && (
                          <p className="text-[11px] font-medium italic text-white/90 border-t border-white/10 pt-1 mt-1">
                            "{event.notes}"
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-start md:items-end gap-0.5 shrink-0">
                        <span className="text-[9px] font-black uppercase tracking-widest text-white/85">
                          {displayDateString}
                        </span>
                        <span className="text-xs font-black flex items-center gap-1 text-white">
                          <Clock size={12} className="text-white/85 shrink-0" />
                          {event.allDay ? 'Dia Inteiro' : `${event.start} - ${event.end}`}
                        </span>
                      </div>
                    </div>
                  );
                })}

              {filteredEvents.length === 0 && (
                <div className="text-center py-10 sm:py-16 bg-slate-50/50 dark:bg-slate-950/10 border border-dashed border-slate-200 dark:border-slate-850 rounded-[2rem]">
                  <p className="text-sm font-bold text-slate-400 dark:text-slate-500">
                    Nenhuma sessão ou compromisso registrado na agenda.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* DIALOG 1: INTERACTIVE CREATE EVENT MODAL */}
      {modalType && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-8 rounded-[2.5rem] shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0 mb-4">
              <div>
                <h4 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                  {editingEventId 
                    ? (modalType === 'appointment' ? 'Editar Agendamento' : modalType === 'compromise' ? 'Editar Compromisso' : 'Editar Bloqueio de Agenda')
                    : (modalType === 'appointment' ? 'Novo Agendamento' : modalType === 'compromise' ? 'Novo Compromisso' : 'Novo Bloqueio de Agenda')
                  }
                </h4>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-1">Preencha as informações para salvar</p>
              </div>
              <button 
                onClick={() => { setModalType(null); setEditingEventId(null); }}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveEvent} className="flex-1 flex flex-col overflow-hidden min-h-0">
              {overlappingEventsPending ? (
                <div className="flex-1 flex flex-col justify-between overflow-hidden">
                  <div className="flex-1 overflow-y-auto space-y-4 pb-4 pr-2 custom-scrollbar">
                    <div className="p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20 flex items-start gap-3">
                      <AlertCircle className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" size={20} />
                      <div>
                        <h5 className="text-xs font-black text-amber-800 dark:text-amber-200 uppercase tracking-wider">Aviso de Sobreposição</h5>
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                          Este compromisso conflita com agendamento(s) de pacientes. Se você continuar, os seguintes atendimentos serão <strong className="font-extrabold text-red-600 dark:text-red-400 uppercase">excluídos definitivamente</strong>:
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Atendimentos Sobrepostos</p>
                      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 custom-scrollbar">
                        {overlappingEventsPending.events.map(ev => {
                          const dateObj = new Date(ev.date + 'T12:00:00');
                          const formattedDate = dateObj.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
                          return (
                            <div key={ev.id} className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/40 dark:border-slate-700/60 flex justify-between items-center text-xs">
                              <div>
                                <p className="font-black text-slate-800 dark:text-slate-100">{ev.title.split('|')[0].trim()}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">Psicoterapia</p>
                              </div>
                              <div className="text-right shrink-0 ml-4">
                                <p className="font-black text-slate-700 dark:text-slate-200 uppercase">{formattedDate}</p>
                                <p className="text-[10px] text-slate-500 font-bold">{ev.allDay ? 'Dia Inteiro' : `${ev.start} - ${ev.end}`}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
                    <button
                      type="button"
                      onClick={() => setOverlappingEventsPending(null)}
                      className="flex-1 py-3 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer text-center"
                    >
                      Ajustar Horário
                    </button>
                    <button
                      type="button"
                      onClick={confirmSaveOverlap}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer shadow-md shadow-red-600/10 text-center"
                    >
                      Confirmar e Excluir
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto pr-2 space-y-4 pb-4 min-h-0 custom-scrollbar">
                  
                  {/* Conditional Patient Dropdown for Appointments */}
                  {modalType === 'appointment' ? (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Paciente</label>
                      <PatientSearchSelect
                        required
                        value={formPatientId}
                        onChange={setFormPatientId}
                        patients={filteredPatientsForAppointment}
                      />
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Título / Motivo</label>
                      <input
                        type="text"
                        required
                        value={formTitle}
                        onChange={(e) => setFormTitle(e.target.value)}
                        placeholder={modalType === 'compromise' ? 'Ex: Visita Técnica, Almoço' : 'Ex: Feriado, Compromisso Médico'}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                      />
                    </div>
                  )}

                  {/* Date Input */}
                  {!formIsPeriod ? (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Data</label>
                      <input
                        type="date"
                        required
                        value={formDate}
                        onChange={(e) => setFormDate(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Data de Início</label>
                        <input
                          type="date"
                          required
                          value={formDate}
                          onChange={(e) => {
                            setFormDate(e.target.value);
                            if (!formEndDate || formEndDate < e.target.value) {
                              setFormEndDate(e.target.value);
                            }
                          }}
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Data de Término</label>
                        <input
                          type="date"
                          required
                          value={formEndDate}
                          min={formDate}
                          onChange={(e) => setFormEndDate(e.target.value)}
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                        />
                      </div>
                    </div>
                  )}



                  {/* Toggle to Block Period of Time (vários dias) for Compromisso */}
                  {modalType === 'compromise' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-4 bg-amber-500/5 rounded-2xl border border-amber-500/10">
                        <div className="space-y-0.5">
                          <p className="text-xs font-bold text-amber-800 dark:text-amber-200">Bloquear período (vários dias)</p>
                          <p className="text-[10px] text-amber-600/80 dark:text-amber-400/85">Definir data de início e término do compromisso</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer select-none">
                          <input 
                            type="checkbox" 
                            checked={formIsPeriod} 
                            onChange={(e) => {
                              setFormIsPeriod(e.target.checked);
                              if (e.target.checked) {
                                setFormEndDate(formDate || formatDateString(currentDate));
                              }
                            }}
                            className="sr-only peer" 
                          />
                          <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200/40 dark:border-slate-700">
                        <div className="space-y-0.5">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">Travar dia inteiro</p>
                          <p className="text-[10px] text-slate-400">Ocupar a agenda durante o dia todo</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer select-none">
                          <input 
                            type="checkbox" 
                            checked={formAllDay} 
                            onChange={(e) => {
                              setFormAllDay(e.target.checked);
                              if (e.target.checked) {
                                setFormStart('00:00');
                                setFormEnd('23:59');
                              } else {
                                setFormStart('09:00');
                                setFormEnd('10:00');
                              }
                            }}
                            className="sr-only peer" 
                          />
                          <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                    </div>
                  )}

              {/* Start & End Times */}
              {!formAllDay && !formIsPeriod && (
                <div className="grid grid-cols-2 gap-4 animate-in fade-in duration-200">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Horário Início</label>
                    <div className="relative">
                      <input
                        type="time"
                        required
                        value={formStart}
                        onChange={(e) => setFormStart(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-10 pr-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      />
                      <Clock size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Horário Término</label>
                    <div className="relative">
                      <input
                        type="time"
                        required
                        value={formEnd}
                        onChange={(e) => setFormEnd(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-10 pr-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      />
                      <Clock size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                </div>
              )}

              {/* Appointment Specific selectors */}
              {modalType === 'appointment' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Modalidade</label>
                      <select
                        value={formModality}
                        onChange={(e) => setFormModality(e.target.value as any)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      >
                        <option value="PRESENCIAL">PRESENCIAL</option>
                        <option value="ONLINE">ONLINE</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Local de Atendimento</label>
                      {locationsForModality.length > 0 ? (
                        /* Bloco 44: só os locais ativos cadastrados para a modalidade escolhida. */
                        <select
                          value={formLocation}
                          onChange={(e) => setFormLocation(e.target.value)}
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                        >
                          {locationsForModality.map((l) => (
                            <option key={l.id} value={l.name}>{l.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={formLocation}
                          onChange={(e) => setFormLocation(e.target.value)}
                          placeholder={formModality === 'ONLINE' ? 'Nenhum local online cadastrado' : 'Nenhum local presencial cadastrado'}
                          title="Cadastre locais em Agenda > Configurações da Agenda > Locais de Atendimento"
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                        />
                      )}
                    </div>
                  </div>

                  {/* Recorrência e Tipo de Serviço (Configurações > Serviços Oferecidos) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Recorrência</label>
                      <select
                        value={formRecurrence}
                        onChange={(e) => setFormRecurrence(e.target.value as any)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      >
                        <option value="NONE">Não se repete</option>
                        <option value="WEEKLY">Semanal</option>
                        <option value="BIWEEKLY">Quinzenal</option>
                        <option value="MONTHLY">Mensal</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tipo de Serviço</label>
                        <span className="text-[9px] font-bold text-indigo-500" title="Cadastrado em Agenda > Configurações da Agenda > Serviços Oferecidos">
                          {services.length ? 'Configurado' : 'Nenhum cadastrado'}
                        </span>
                      </div>
                      <select
                        value={formServiceId}
                        onChange={(e) => selectService(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                      >
                        {!services.length && <option value="">Cadastre em Configurações da Agenda</option>}
                        {services.map((svc) => (
                          <option key={svc.id} value={svc.id}>
                            {svc.name} ({svc.duration} min)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Valor (preenchido pelo serviço, ainda editável) + Notificar */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Valor do Serviço</label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">R$</span>
                        <input
                          type="text"
                          value={formServiceValue}
                          onChange={(e) => setFormServiceValue(e.target.value)}
                          placeholder="150,00"
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-9 pr-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800">
                      <div className="space-y-0.5 min-w-0">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">Notificar paciente</p>
                        <p className="text-[10px] text-slate-400 truncate">Via WhatsApp ao salvar</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0">
                        <input 
                          type="checkbox" 
                          checked={formNotifyPatient} 
                          onChange={(e) => setFormNotifyPatient(e.target.checked)}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Recurrence Repeat count input */}
                  {formRecurrence !== 'NONE' && (
                    <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Número de Repetições (Sessões)</label>
                      <input
                        type="number"
                        min={1}
                        max={52}
                        required
                        value={formRecurrenceCount}
                        onChange={(e) => setFormRecurrenceCount(Number(e.target.value))}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                      />
                      <p className="text-[10px] text-slate-400 italic">Serão criados {formRecurrenceCount} agendamentos no calendário.</p>
                    </div>
                  )}

                </>
              )}

              {/* Compromisso Specific selectors */}
              {modalType === 'compromise' && !formIsPeriod && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Recorrência</label>
                    <select
                      value={formRecurrence}
                      onChange={(e) => setFormRecurrence(e.target.value as any)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                    >
                      <option value="NONE">Não se repete</option>
                      <option value="WEEKLY">Semanal</option>
                      <option value="BIWEEKLY">Quinzenal</option>
                      <option value="MONTHLY">Mensal</option>
                    </select>
                  </div>

                  {formRecurrence !== 'NONE' && (
                    <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Número de Repetições</label>
                      <input
                        type="number"
                        min={1}
                        max={52}
                        required
                        value={formRecurrenceCount}
                        onChange={(e) => setFormRecurrenceCount(Number(e.target.value))}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                      />
                      <p className="text-[10px] text-slate-400 italic">Serão criados {formRecurrenceCount} compromissos no calendário.</p>
                    </div>
                  )}
                </>
              )}

              {/* Notes Field */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Observações</label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Instruções ou anotações adicionais..."
                  rows={2}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                />
              </div>

              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
                <button
                  type="button"
                  onClick={() => { setModalType(null); setEditingEventId(null); }}
                  className="flex-1 py-3 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer text-center"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingEvent}
                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2"
                >
                  <Check size={14} /> {isSavingEvent ? 'Salvando...' : 'Salvar na Agenda'}
                </button>
              </div>
            </>
          )}
        </form>
          </div>
        </div>
      )}

      {/* Bloco #73: cancelar só o dia ou toda a recorrência */}
      {scopePrompt && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true" aria-labelledby="scope-prompt-title">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-8 rounded-[2.5rem] shadow-2xl w-full max-w-md space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 shrink-0"><Repeat size={22} /></div>
              <div className="space-y-2">
                <h4 id="scope-prompt-title" className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">Este horário se repete</h4>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">
                  Deseja cancelar apenas este dia ou toda a recorrência? Na recorrência, saem este dia e os seguintes; datas passadas e sessões com presença registrada ficam.
                </p>
                <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">
                  {scopePrompt.title.split('|')[0].trim()} · {scopePrompt.date.split('-').reverse().join('/')} às {scopePrompt.start}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => continueCancel(scopePrompt, 'one')}
                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-100 rounded-2xl text-[11px] font-black uppercase tracking-wider"
              >
                Cancelar apenas este dia
              </button>
              <button
                type="button"
                onClick={() => continueCancel(scopePrompt, 'series')}
                className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl text-[11px] font-black uppercase tracking-wider"
              >
                Cancelar toda a recorrência
              </button>
            </div>
            <button
              type="button"
              onClick={() => setScopePrompt(null)}
              className="w-full text-[11px] font-black uppercase tracking-wider text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Voltar
            </button>
          </div>
        </div>
      )}

      {/* DIALOG 2: INTERACTIVE EVENT DETAIL/DELETE MODAL - Allodos Styled */}
      {cancelPrompt && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true" aria-labelledby="cancel-prompt-title">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-8 rounded-[2.5rem] shadow-2xl w-full max-w-md space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 shrink-0"><MessageCircle size={22} /></div>
              <div className="space-y-2">
                <h4 id="cancel-prompt-title" className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">Cancelar agendamento</h4>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">
                  {cancelScope === 'series'
                    ? 'Deseja enviar uma mensagem via WhatsApp ao paciente informando o cancelamento das próximas sessões desta recorrência, com o link da agenda para reagendamento?'
                    : 'Deseja enviar uma mensagem via WhatsApp ao paciente informando sobre o cancelamento, com o link da agenda para reagendamento?'}
                </p>
                <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">
                  {cancelPrompt.title.split('|')[0].trim()} · {cancelPrompt.date.split('-').reverse().join('/')} às {cancelPrompt.start}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                disabled={isCancelling}
                onClick={async () => {
                  setIsCancelling(true);
                  await handleDeleteEvent(cancelPrompt.id, true, cancelScope);
                  setIsCancelling(false);
                  setCancelPrompt(null);
                }}
                className="flex-1 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
              >
                Sim, enviar mensagem
              </button>
              <button
                type="button"
                disabled={isCancelling}
                onClick={async () => {
                  setIsCancelling(true);
                  await handleDeleteEvent(cancelPrompt.id, false, cancelScope);
                  setIsCancelling(false);
                  setCancelPrompt(null);
                }}
                className="flex-1 py-3.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 border border-rose-200/40 dark:border-rose-900/30 rounded-2xl text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
              >
                Não enviar
              </button>
            </div>
            <button
              type="button"
              disabled={isCancelling}
              onClick={() => setCancelPrompt(null)}
              className="w-full text-[11px] font-black uppercase tracking-wider text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Voltar sem cancelar
            </button>
          </div>
        </div>
      )}

      {selectedEvent && (
        <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-8 rounded-[2.5rem] shadow-2xl w-full max-w-lg space-y-6 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            {/* Header: Title, Close button, Help icon */}
            <div className="flex justify-between items-start border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md ${
                    selectedEvent.type === 'appointment' 
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400' 
                      : selectedEvent.type === 'compromise' 
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400' 
                        : 'bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400'
                  }`}>
                    {selectedEvent.type === 'appointment' ? 'Atendimento Clínico' : selectedEvent.type === 'compromise' ? 'Compromisso Pessoal' : 'Bloqueio de Agenda'}
                  </span>
                  {selectedEvent.patientResponse && (
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md ${
                      selectedEvent.patientResponse === 'CONFIRMADO'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400'
                    }`}>
                      {selectedEvent.patientResponse === 'CONFIRMADO' ? 'Paciente confirmou' : 'Cancelado pelo paciente'}
                    </span>
                  )}
                </div>
                <h4 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight mt-2">
                  {selectedEvent.type === 'appointment' ? 'Detalhes do Agendamento' : 'Detalhes do Compromisso'}
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => {
                    setToastMessage({ text: "Dica: Use as ações rápidas abaixo para atualizar o status do agendamento ou compromisso.", type: "info" });
                  }}
                  className="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
                  title="Ajuda"
                >
                  <HelpCircle size={18} />
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setSelectedEvent(null);
                    setShowPatientProfileInline(false);
                    setShowSessionNotesForm(false);
                  }}
                  className="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {selectedEvent.type === 'appointment' ? (
              /* APPOINTMENT VIEW (FULL DETAILS) */
              <div className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-3xl border border-slate-100 dark:border-slate-850">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Data e Horário</p>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-100 mt-1">
                    {new Date(selectedEvent.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">
                    {selectedEvent.allDay ? 'Dia Inteiro' : `das ${selectedEvent.start} às ${selectedEvent.end}`}
                  </p>
                </div>

                {/* Event Title */}
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Paciente</p>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-200">
                    {`Paciente ${selectedEvent.patientName || selectedEvent.title.split('|')[0].trim()} | ${selectedEvent.serviceName || 'Psicoterapia'} | ${selectedEvent.modality === 'ONLINE' ? 'Internet' : 'Presencial'}`}
                  </p>
                </div>



                {/* Service & Price */}
                <div className="grid grid-cols-2 gap-4 border-t border-b border-slate-100 dark:border-slate-800 py-3.5">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Serviço</p>
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-1">
                      {selectedEvent.serviceName || 'Psicoterapia Individual'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Valor Estimado</p>
                    <p className="text-xs font-extrabold text-slate-800 dark:text-slate-100 mt-1">
                      R$ {selectedEvent.serviceValue
                        || formatPrice(services.find((s) => s.name === selectedEvent.serviceName)?.price)
                        || '150,00'}
                    </p>
                  </div>
                </div>

                {/* Local */}
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Local de Atendimento</p>
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 leading-relaxed">
                    {selectedEvent.modality === 'ONLINE' 
                      ? (selectedEvent.locationName || 'Plataforma Online (Sala de Teleconsulta Virtual Segura)')
                      : selectedEvent.locationName || 'Consultório Principal - Avenida Júlio de Castilhos, 1135, SOBRELOJA. Centro. Caxias do Sul - RS.'
                    }
                  </p>
                  {/* Blocos #56/#57: o link que vai ao paciente ao clicar em "Acessar Sala" */}
                  {selectedEvent.meetLink ? (
                    <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 break-all">
                      Sala do Meet:{' '}
                      <a href={selectedEvent.meetLink} target="_blank" rel="noopener noreferrer" className="underline">{selectedEvent.meetLink}</a>
                    </p>
                  ) : selectedEvent.modality === 'ONLINE' && (
                    roomLinkFor(selectedEvent) ? (
                      <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 break-all">Sala: {roomLinkFor(selectedEvent)}</p>
                    ) : (
                      googleConnected ? (
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">A sala do Google Meet é criada ao clicar em "Acessar Sala".</p>
                      ) : (
                        <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300">Sem sala vinculada: o paciente não será avisado ao acessar. Conecte o Google na aba Teleconsulta ou cadastre o link no local.</p>
                      )
                    )
                  )}
                </div>

                {/* Notes Display */}
                {selectedEvent.notes && (
                  <div className="space-y-1 bg-slate-50 dark:bg-slate-950/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-850">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Observação do Agendamento</p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 italic font-medium">
                      "{selectedEvent.notes}"
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* COMPROMISE VIEW (SIMPLIFIED / ONLY RELEVANT INFO) */
              <div className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-3xl border border-slate-100 dark:border-slate-850">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Data e Horário</p>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-100 mt-1">
                    {new Date(selectedEvent.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">
                    {selectedEvent.allDay ? 'Dia Inteiro' : `das ${selectedEvent.start} às ${selectedEvent.end}`}
                  </p>
                </div>

                {/* Event Title */}
                <div className="space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Descrição do Compromisso</p>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-200">
                    {selectedEvent.title}
                  </p>
                </div>

                {/* Location if set */}
                {selectedEvent.locationName && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Local</p>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                      {selectedEvent.locationName}
                    </p>
                  </div>
                )}

                {/* Notes Display if set */}
                {selectedEvent.notes && (
                  <div className="space-y-1 bg-slate-50 dark:bg-slate-950/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-850">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Observações</p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 italic font-medium">
                      "{selectedEvent.notes}"
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ACTION BUTTONS GRID */}
            <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-850">
              {selectedEvent.type === 'appointment' ? (
                /* BUTTONS FOR APPOINTMENT VIEW */
                <>
                  {/* Row 1: Compareceu / Não Compareceu */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const evento = selectedEvent;
                        setEvents(prev => prev.map(e => e.id === evento.id ? { ...e, status: 'Compareceu' } : e));
                        persistAttendance(evento, 'Compareceu');
                        setSelectedEvent(null);
                        // Comparecimento confirmado gera a entrada no livro caixa (no banco).
                        try {
                          const lancado = await registerAttendanceIncome(evento, true);
                          setToastMessage({
                            text: !lancado
                              ? 'Atendimento confirmado como Compareceu (serviço sem valor: nada lançado no livro caixa).'
                              : lancado.created
                                ? `Atendimento confirmado. Entrada de ${formatCurrency(lancado.amount)} lançada no livro caixa.`
                                : `Atendimento confirmado. A entrada de ${formatCurrency(lancado.amount)} já estava no livro caixa.`,
                            type: 'success',
                          });
                        } catch (err) {
                          setToastMessage({ text: `Compareceu registrado, mas o livro caixa falhou: ${describeError(err)}`, type: 'error' });
                        }
                      }}
                      className="flex items-center justify-center gap-2 py-3 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 border border-indigo-200/50 dark:border-indigo-900/40 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer hover:scale-[1.01]"
                    >
                      <Check size={14} className="text-emerald-600 dark:text-emerald-400" /> Compareceu
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const evento = selectedEvent;
                        setEvents(prev => prev.map(e => e.id === evento.id ? { ...e, status: 'Não Compareceu' } : e));
                        persistAttendance(evento, 'Não Compareceu');
                        // Na falta, quem decide se cobra é o profissional — e é
                        // essa decisão que vira (ou não) entrada no livro caixa.
                        const valor = attendanceValue(evento);
                        let cobrada = false;
                        if (valor > 0) {
                          cobrada = window.confirm(
                            `Cobrar ${formatCurrency(valor)} pela ausência de ${evento.patientName || 'paciente'}?\n\n` +
                            'OK lança a entrada no livro caixa. Cancelar registra a falta sem cobrança.',
                          );
                        }
                        setSelectedEvent(null);
                        try {
                          if (cobrada) await registerAttendanceIncome(evento, false);
                          else if (isUuid(evento.id)) await removeAttendanceIncome(attendanceKind(evento), evento.id);
                          setToastMessage({
                            text: cobrada
                              ? `Falta registrada e cobrada (${formatCurrency(valor)}).`
                              : 'Falta registrada (sem cobrança).',
                            type: 'success',
                          });
                        } catch (err) {
                          setToastMessage({ text: `Falta registrada, mas o livro caixa falhou: ${describeError(err)}`, type: 'error' });
                        }
                      }}
                      className="flex items-center justify-center gap-2 py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-850 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/40 dark:border-slate-700/50 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer hover:scale-[1.01]"
                    >
                      <X size={14} className="text-rose-600 dark:text-rose-400" /> Não Compareceu
                    </button>
                  </div>

                  {/* Row 2: Redirection to Prontuário */}
                  <button
                    type="button"
                    onClick={() => {
                      const matchedPatient = patients.find(p => p.id === selectedEvent.patientId || p.name.toLowerCase() === (selectedEvent.patientName || '').toLowerCase());
                      if (matchedPatient) {
                        window.dispatchEvent(new CustomEvent('navigate-to-patient-prontuario', {
                          detail: { patientId: matchedPatient.id, date: selectedEvent.date }
                        }));
                        setSelectedEvent(null);
                      } else {
                        setToastMessage({ text: 'Paciente não encontrado para este agendamento.', type: 'error' });
                      }
                    }}
                    className="w-full py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-850 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/40 dark:border-slate-700/50 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all text-center flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01]"
                  >
                    <FileText size={14} className="text-slate-400" /> Prontuário
                  </button>

                  {/* Row 3: Editar */}
                  <button
                    type="button"
                    onClick={() => openEditModal(selectedEvent)}
                    className="w-full py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-850 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/40 dark:border-slate-700/50 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all text-center flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01]"
                  >
                    <Pencil size={14} className="text-slate-400" /> Editar
                  </button>

                  {/* Row 4: Acessar Sala — só teleconsulta (blocos #56/#57) */}
                  {(selectedEvent.modality === 'ONLINE' || selectedEvent.modality === 'PRESENCIAL_ONLINE') && (
                  <button
                    type="button"
                    onClick={() => accessRoom(selectedEvent)}
                    className="w-full py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-850 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/40 dark:border-slate-700/50 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer hover:scale-[1.01] text-center flex items-center justify-center gap-2"
                  >
                    <Video size={14} className="text-slate-400" /> Acessar Sala
                  </button>
                  )}

                  {/* Row 5: Cancelar Agenda */}
                  <button
                    type="button"
                    onClick={() => askCancel(selectedEvent)}
                    className="w-full py-3 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 border border-rose-200/30 dark:border-rose-900/30 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01]"
                  >
                    <Trash2 size={14} /> Cancelar Agenda
                  </button>
                </>
              ) : (
                /* BUTTONS FOR COMPROMISE / BLOCKING (SIMPLIFIED) */
                <>
                  {/* Row 1: Editar */}
                  <button
                    type="button"
                    onClick={() => openEditModal(selectedEvent)}
                    className="w-full py-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-850 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/40 dark:border-slate-700/50 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all text-center flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01]"
                  >
                    <Pencil size={14} className="text-slate-400" /> Editar
                  </button>

                  {/* Row 2: Cancelar Agenda */}
                  <button
                    type="button"
                    onClick={() => askCancel(selectedEvent)}
                    className="w-full py-3 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 border border-rose-200/30 dark:border-rose-900/30 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01]"
                  >
                    <Trash2 size={14} /> Cancelar Agenda
                  </button>
                </>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Agenda;
